import { getOpenSignDocument } from "./api";

/**
 * Case-insensitive name matcher that also handles Adjuster <-> Adjustor variation
 */
export const matchesSignerName = (signerName, targetName) => {
	const a = (signerName || "").toLowerCase();
	const b = (targetName || "").toLowerCase();
	if (a === b) return true;
	// Handle Adjuster <-> Adjustor spelling variation
	if (
		(a === "adjuster" || a === "adjustor") &&
		(b === "adjuster" || b === "adjustor")
	)
		return true;
	return false;
};

/**
 * Check if a signer has already signed by checking the audit trail.
 * Matches by objectId first (most reliable), then by name as fallback.
 */
export const hasSignerSigned = (signer, auditTrail) => {
	if (!signer) return false;
	return auditTrail.some((entry) => {
		if (entry.Activity !== "Signed") return false;
		const auditSignerId = entry.UserPtr?.objectId;
		const signerId = signer?.objectId;
		// When both IDs exist they are authoritative. Falling through to a name
		// match after different IDs caused two contacts both named "Claimant" to
		// be marked signed when only one had signed.
		if (auditSignerId && signerId) return auditSignerId === signerId;
		// Older audit records may not contain an ID, so retain the name fallback
		// only when at least one side has no stable identity.
		if (
			entry.UserPtr?.Name &&
			matchesSignerName(entry.UserPtr.Name, signer.Name)
		)
			return true;
		return false;
	});
};

const claimantRoleRank = (role, fallbackIndex) => {
	const match = (role || "").match(
		/^claimant(?:\s*\(([a-z])\)|\s+([a-z])|\s*\((\d+)\)|\s+(\d+))?$/i,
	);
	if (!match) return fallbackIndex;
	const letter = match[1] || match[2];
	if (letter) return letter.toUpperCase().charCodeAt(0) - 65;
	const number = match[3] || match[4];
	if (number) return Number(number) - 1;
	return fallbackIndex;
};

/**
 * Resolves the exact OpenSign recipient assigned to a claimant-specific link.
 * Claimants are ordered primary-first/by database ID and paired with template
 * roles such as Claimant (A), Claimant (B), etc.
 */
export const getClaimantSignerForParty = (docDetails, claimants, partyId) => {
	if (!partyId) return null; // Legacy assignment-only links use sequential fallback.

	const claimantIndex = (claimants || []).findIndex(
		(claimant) => String(claimant.partyId) === String(partyId),
	);
	if (claimantIndex < 0) {
		throw new Error("This signing link does not match a claimant on the assignment.");
	}

	const claimantPlaceholders = (docDetails?.Placeholders || [])
		.map((placeholder, index) => ({
			...placeholder,
			_templateIndex: index,
			objectId:
				placeholder.signerObjId || placeholder.signerPtr?.objectId || null,
		}))
		.filter(
			(placeholder) =>
				/^claimant\b/i.test(placeholder.Role || "") &&
				placeholder.objectId,
		)
		.filter(
			(placeholder, index, rows) =>
				rows.findIndex((row) => row.Role === placeholder.Role) === index,
		)
		.sort(
			(a, b) =>
				claimantRoleRank(a.Role, a._templateIndex) -
				claimantRoleRank(b.Role, b._templateIndex),
		);

	if (claimantPlaceholders.length !== (claimants || []).length) {
		throw new Error(
			`The DV template has ${claimantPlaceholders.length} claimant signer role(s), but this assignment has ${(claimants || []).length} claimant(s).`,
		);
	}

	const selectedPlaceholder = claimantPlaceholders[claimantIndex];
	const duplicateRecipient = claimantPlaceholders.find(
		(placeholder, index) =>
			index !== claimantIndex &&
			placeholder.objectId === selectedPlaceholder.objectId,
	);
	if (duplicateRecipient) {
		throw new Error(
			`DV template roles "${selectedPlaceholder.Role}" and "${duplicateRecipient.Role}" use the same OpenSign recipient. Assign a different recipient to each claimant role.`,
		);
	}

	const signer = (docDetails?.Signers || []).find(
		(candidate) => candidate.objectId === selectedPlaceholder.objectId,
	);
	return {
		...(signer || selectedPlaceholder.signerPtr || {}),
		objectId: selectedPlaceholder.objectId,
		Name: selectedPlaceholder.Role || signer?.Name || "Claimant",
	};
};

/**
 * Selects the signer reached through a claimant's WhatsApp link.
 * Each link remains locked to its assigned claimant until every claimant signs.
 * After that, any claimant link may continue through the remaining witness,
 * payee, or custom signing roles in template order.
 */
export const getSignerForClaimantLink = (docDetails, claimants, partyId) => {
	const claimantWithoutPartyId = (claimants || []).find(
		(claimant) => !claimant.partyId,
	);
	if (claimantWithoutPartyId) {
		throw new Error(
			`${claimantWithoutPartyId.insuredName || "A claimant"} has no party ID. Save the claim again after applying the claimant party-ID hotfix, then resend the WhatsApp links.`,
		);
	}

	const claimantSigner = getClaimantSignerForParty(
		docDetails,
		claimants,
		partyId,
	);
	const auditTrail = docDetails?.AuditTrail || [];
	const documentSigners = docDetails?.Signers || [];
	if (
		docDetails?.IsCompleted === true &&
		documentSigners.some(
			(signer) => !hasSignerSigned(signer, auditTrail),
		)
	) {
		throw new Error(
			"OpenSign completed this document before every recipient signed. The Adjuster must recreate the Acceptance Form and resend the WhatsApp links.",
		);
	}
	if (!hasSignerSigned(claimantSigner, auditTrail)) return claimantSigner;

	const claimantSigners = (claimants || []).map((claimant) =>
		getClaimantSignerForParty(docDetails, claimants, claimant.partyId),
	);
	if (
		!claimantSigners.every((signer) => hasSignerSigned(signer, auditTrail))
	) {
		return null;
	}

	const secondarySigners = (docDetails?.Placeholders || [])
		.filter((placeholder) => {
			const role = (placeholder.Role || "").trim();
			return (
				role &&
				!/^claimant\b/i.test(role) &&
				!/^adjust(?:er|or)\b/i.test(role) &&
				!/^prefill$/i.test(role)
			);
		})
		.map((placeholder) => {
			const objectId =
				placeholder.signerObjId || placeholder.signerPtr?.objectId || null;
			const signer = (docDetails?.Signers || []).find(
				(candidate) => candidate.objectId === objectId,
			);
			return objectId
				? {
						...(signer || placeholder.signerPtr || {}),
						objectId,
						Name: placeholder.Role || signer?.Name || "Witness",
				  }
				: null;
		})
		.filter(Boolean)
		.filter(
			(signer, index, rows) =>
				rows.findIndex((row) => row.objectId === signer.objectId) === index,
		);

	const sharedRecipient = secondarySigners.find((signer) =>
		claimantSigners.some(
			(claimant) => claimant.objectId === signer.objectId,
		),
	);
	if (sharedRecipient) {
		const sharedClaimant = claimantSigners.find(
			(claimant) => claimant.objectId === sharedRecipient.objectId,
		);
		throw new Error(
			`DV template roles "${sharedClaimant.Name}" and "${sharedRecipient.Name}" use the same OpenSign recipient. Assign a different recipient to each signing role.`,
		);
	}

	return (
		secondarySigners.find(
			(signer) => !hasSignerSigned(signer, auditTrail),
		) || null
	);
};

/**
 * Gets a signer's URL for a document based on user role.
 * ALWAYS returns a /load/recipientSignPdf URL — never placeholdersign.
 *
 * For authenticated eMacs adjustors: returns ONLY the adjustor signer's URL.
 *   If the adjustor has already signed, returns null (does NOT fall through to other signers).
 * For external/token users: returns the next unsigned signer's URL in sequence.
 *
 * @param {string} documentId - OpenSign document ID
 * @param {Object} currentUser - The current user object
 * @param {string} [preferredSignerName] - Optional preferred signer name to find
 * @param {boolean} [isTokenAccess=false] - True when accessed via external token link
 * @param {string} [preferredSignerId] - Exact recipient ID encoded by a claimant-specific link
 * @returns {Promise<string|null>} The signing URL, or null if the authenticated user has already signed
 */
export const getSigningUrlForUser = async (
	documentId,
	currentUser,
	preferredSignerName = null,
	isTokenAccess = false,
	preferredSignerId = null,
) => {
	const docDetails = await getOpenSignDocument(documentId);
	if (!docDetails) {
		throw new Error("Could not fetch document details");
	}

	const baseUrl = process.env.REACT_APP_OPENSIGN_URL;
	if (!baseUrl) {
		throw new Error("OpenSign URL is not configured");
	}

	let signers = docDetails.Signers || [];
	const auditTrail = docDetails.AuditTrail || [];

	// Fallback: if Signers is empty, extract signer info from Placeholders
	if (signers.length === 0 && docDetails.Placeholders?.length > 0) {
		signers = docDetails.Placeholders.filter(
			(p) => p.signerObjId || p.signerPtr?.objectId,
		)
			.map((p) => ({
				objectId: p.signerObjId || p.signerPtr?.objectId,
				Name: p.Role || p.signerPtr?.Name || "",
				Email: p.signerPtr?.Email || "",
			}))
			// Dedupe by objectId
			.filter(
				(s, i, arr) =>
					arr.findIndex((x) => x.objectId === s.objectId) === i,
			);
		console.log("Signers extracted from Placeholders:", signers);
	}

	console.log(
		"Signer selection — signers:",
		signers.map((s) => ({ Name: s.Name, objectId: s.objectId })),
		"auditTrail signed:",
		auditTrail
			.filter((e) => e.Activity === "Signed")
			.map((e) => e.UserPtr?.Name),
	);

	// Helper to extract the contactBookId from a signer object
	const getSignerContactId = (signer) => {
		if (!signer) return null;
		if (signer.objectId) return signer.objectId;
		return (
			signer.signerContactId ||
			signer.signerId ||
			signer.contactId ||
			signer.id ||
			(signer.contact && signer.contact.objectId) ||
			(signer.UserId &&
				typeof signer.UserId === "object" &&
				signer.UserId.objectId) ||
			null
		);
	};

	// Build recipientSignPdf URL (the only URL type we should ever use)
	const buildSignUrl = (contactId) =>
		`${baseUrl}/load/recipientSignPdf/${documentId}/${contactId}`;

	let targetSigner = null;
	if (preferredSignerId) {
		const intendedSigner = signers.find(
			(signer) => getSignerContactId(signer) === preferredSignerId,
		);
		if (!intendedSigner) {
			throw new Error("The intended claimant signer is missing from the document.");
		}
		if (hasSignerSigned(intendedSigner, auditTrail)) return null;
		targetSigner = intendedSigner;
	}

	// Step 1: Try the preferred signer name (from determineSignerName) — must be unsigned
	if (!targetSigner && preferredSignerName) {
		targetSigner = signers.find(
			(s) =>
				matchesSignerName(s.Name, preferredSignerName) &&
				!hasSignerSigned(s, auditTrail),
		);
		if (targetSigner) {
			console.log(
				`Signer selected by preferred name "${preferredSignerName}": ${targetSigner.Name}`,
			);
		}
	}

	// Step 2: For authenticated adjustors (NOT token access), ONLY show their own signer link.
	// If they've already signed, return null — do NOT fall through to other signers.
	if (!targetSigner && currentUser?.userName && !isTokenAccess) {
		const adjustorSigner = signers.find(
			(s) =>
				matchesSignerName(s.Name, "Adjustor") ||
				matchesSignerName(s.Name, "Adjuster"),
		);
		if (adjustorSigner) {
			if (!hasSignerSigned(adjustorSigner, auditTrail)) {
				targetSigner = adjustorSigner;
				console.log(
					`Signer selected for authenticated adjustor: ${adjustorSigner.Name}`,
				);
			} else {
				// Authenticated adjustor has already signed — do NOT give them another signer's link
				console.log(
					"Authenticated adjustor has already signed. Not providing another signer's URL.",
				);
				return null;
			}
		}
	}

	// Step 3: Sequential — find the next unsigned signer in the expected signing order
	// (Only reached for token/external users or when no adjustor signer exists)
	if (!targetSigner) {
		const signingOrder = ["Adjustor", "Adjuster", "Claimant", "Witness"];
		for (const roleName of signingOrder) {
			const signer = signers.find(
				(s) =>
					matchesSignerName(s.Name, roleName) &&
					!hasSignerSigned(s, auditTrail),
			);
			if (signer) {
				targetSigner = signer;
				console.log(`Signer selected by signing order: ${signer.Name}`);
				break;
			}
		}
	}

	// Step 4: Any unsigned signer at all
	if (!targetSigner) {
		targetSigner = signers.find((s) => !hasSignerSigned(s, auditTrail));
		if (targetSigner) {
			console.log(`Signer selected (any unsigned): ${targetSigner.Name}`);
		}
	}

	// Step 5: All signers have signed — document is complete, return null
	if (!targetSigner && signers.length > 0) {
		console.log(
			"All signers have signed. Document is complete — not returning a signing URL.",
		);
		return null;
	}

	if (targetSigner) {
		const contactId = getSignerContactId(targetSigner);
		if (contactId) {
			const url = buildSignUrl(contactId);
			console.log(`Generated signing URL: ${url}`);
			return url;
		}
		console.error("Could not extract contactId from signer:", targetSigner);
	}

	throw new Error(
		"No valid signer found in the document. The document may not have signers assigned.",
	);
};

/**
 * Ensures an OpenSign document is finalized and ready for signing
 * This checks if the document is in draft state and finalizes it if necessary
 *
 * @param {string} documentId - The OpenSign document ID
 * @returns {Promise<boolean>} - True if document is finalized successfully
 */
export const ensureDocumentIsFinalized = async (documentId) => {
	try {
		// Get document details
		const { getOpenSignDocument, finalizeOpenSignDocument } =
			await import("./api");

		let docDetails;
		try {
			docDetails = await getOpenSignDocument(documentId);
		} catch (error) {
			console.warn(
				"Could not get document details for finalization, attempting minimal finalization:",
				error.message,
			);
			try {
				await finalizeOpenSignDocument(documentId, null);
			} catch (finalizeError) {
				console.warn(
					"Minimal finalization also failed, document may already be in correct state:",
					finalizeError.message,
				);
			}
			return true;
		}

		if (!docDetails) {
			console.warn(
				"No document details found, attempting minimal finalization",
			);
			try {
				await finalizeOpenSignDocument(documentId, null);
			} catch (finalizeError) {
				console.warn(
					"Minimal finalization failed:",
					finalizeError.message,
				);
			}
			return true;
		}

		// If document is already active/finalized, skip finalization
		if (
			docDetails.Status === "active" &&
			docDetails.SentToOthers === true
		) {
			console.log(
				"Document is already finalized, skipping finalization update",
			);
			return true;
		}

		// If all signers have signed (document is completed), do NOT re-finalize
		const auditTrail = docDetails.AuditTrail || [];
		const allSigners = docDetails.Signers || [];
		if (allSigners.length > 0) {
			const allHaveSigned = allSigners.every((signer) =>
				hasSignerSigned(signer, auditTrail),
			);
			if (allHaveSigned) {
				console.log(
					"All signers have completed signing, skipping finalization to preserve completed state",
				);
				return true;
			}
		}

		// Prepare document data for finalization by copying most of the original document structure
		const documentData = {
			Name: docDetails.Name || `Document ${documentId}`,
			Placeholders: docDetails.Placeholders || [],
			Signers: docDetails.Signers || [],
			SentToOthers: true,
			Status: "active",
			Stage: "review",
		};

		// Keep existing SignatureType or set defaults
		if (docDetails.SignatureType) {
			documentData.SignatureType = docDetails.SignatureType;
		} else {
			documentData.SignatureType = [
				{ name: "draw", enabled: true },
				{ name: "typed", enabled: true },
				{ name: "upload", enabled: true },
				{ name: "default", enabled: true },
			];
		}

		// Always set ExpiryDate to ensure it exists
		// Create a new expiry date 30 days from now
		const expiryDate = new Date();
		expiryDate.setDate(expiryDate.getDate() + 30);
		documentData.ExpiryDate = {
			__type: "Date",
			iso: expiryDate.toISOString(),
		};

		// Add SignedUrl if it exists
		if (docDetails.SignedUrl) {
			documentData.SignedUrl = docDetails.SignedUrl;
		}

		// Finalize the document - if it fails (e.g., 404), the document may still be usable
		try {
			await finalizeOpenSignDocument(documentId, documentData);
		} catch (finalizeError) {
			console.warn(
				"Finalization update failed, document may already be in correct state:",
				finalizeError.message,
			);
		}
		return true;
	} catch (error) {
		console.error("Error finalizing document:", error);
		return false;
	}
};
